import React, { useCallback, useEffect, useState } from 'react';
import companyService, { Angebot, CompanyProfile } from '../services/companyService';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { StatusBadge } from '../components/ui';
import { zeit, fehlerText } from './format';

/**
 * Account-Profil in der Firmenakte: Anlass, Einordnung, passendes Angebot und
 * der Einstieg, der als {{Einstieg}} in die Kampagne geht.
 *
 * "Intern" (Finanzen, bekannte Probleme) steht bewusst abgesetzt und mit
 * Warnhinweis: das ist Vorbereitung fuer ein Gespraech, nie Stoff fuer eine
 * Mail.
 */

/**
 * Quellen stammen aus der Modellantwort und damit mittelbar aus dem Netz. Nur
 * http(s) wird verlinkt -- ein "javascript:"-Link waere sonst ausfuehrbar.
 */
export const sichereUrl = (url?: string | null): string | null =>
  url && /^https?:\/\/[^\s"'<>]+$/i.test(url.trim()) ? url.trim() : null;

const QuellenLink: React.FC<{ url?: string | null; children?: React.ReactNode }> = ({ url, children }) => {
  const sicher = sichereUrl(url);
  if (!sicher) return <>{children ?? url ?? null}</>;
  return <a href={sicher} target="_blank" rel="noreferrer noopener">{children ?? sicher}</a>;
};

const ANGEBOT_LABEL: Record<Angebot, string> = {
  grc: 'GRC-Plattform',
  pentest: 'Pentest',
  assessment: 'Security-Assessment',
  keins: 'kein passendes Angebot',
};

export const AccountProfil: React.FC<{ companyId: string }> = ({ companyId }) => {
  // Welle 2: POST /companies/:id/profil verlangt vertrieb.bearbeiten; Lesende sehen nur das Profil.
  const { darf } = useBerechtigung();
  const darfErstellen = darf('vertrieb.bearbeiten');
  const [profil, setProfil] = useState<CompanyProfile | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [erstellt, setErstellt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setProfil(await companyService.getProfil(companyId));
    } catch (e) {
      setFehler(fehlerText(e, 'Profil konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [companyId]);

  useEffect(() => { laden(); }, [laden]);

  const erstellen = async () => {
    setErstellt(true);
    setFehler(null);
    setHinweis(null);
    try {
      const r = await companyService.erstelleProfil(companyId);
      setProfil(r.profil);
      if (r.einstiegVerworfen) setHinweis(`Einstieg verworfen (${r.einstiegVerworfen}); die Mail nutzt den Faktensatz.`);
    } catch (e) {
      setFehler(fehlerText(e, 'Profil konnte nicht erstellt werden'));
    } finally {
      setErstellt(false);
    }
  };

  const liste = <T,>(w: T[] | null | undefined): T[] => (Array.isArray(w) ? w : []);

  return (
    <section className="akte-block" aria-label="Account-Profil">
      <div className="akte-block-kopf">
        <h2>Account-Profil</h2>
        {darfErstellen && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={erstellen} disabled={erstellt}>
            {erstellt ? 'Recherchiere … (bis 90 s)' : profil ? 'Aktualisieren' : 'Profil erstellen'}
          </button>
        )}
      </div>

      {fehler && <p className="form-error" role="alert">{fehler}</p>}
      {hinweis && <p className="form-helper">{hinweis}</p>}
      {laedt && <p className="inbox-item-hinweis">Lädt …</p>}
      {!laedt && !profil && !erstellt && (
        <p className="inbox-item-hinweis">
          {darfErstellen
            ? 'Noch kein Profil. „Profil erstellen“ recherchiert öffentliche Quellen (rund 3 Cent) und schlägt Anlass, Angebot und Einstieg vor.'
            : 'Noch kein Profil.'}
        </p>
      )}

      {profil && (
        <div className="profil">
          <p className="form-helper">
            Stand {zeit(profil.createdAt)}{profil.modell && <> · {profil.modell}</>}{profil.sitzLand && <> · Sitz {profil.sitzLand}</>}{profil.branchenbezug && <> · Branche „{profil.branchenbezug}“</>}
            {profil.groesse && <> · {profil.groesse}</>}
          </p>

          {profil.angebot && (
            <p>
              <StatusBadge tone={profil.angebot === 'keins' ? 'neutral' : 'primary'}>{ANGEBOT_LABEL[profil.angebot]}</StatusBadge>
              {profil.angebotBegruendung && <> {profil.angebotBegruendung}</>}
            </p>
          )}

          {profil.einstieg ? (
            <blockquote className="profil-einstieg" aria-label="Einstieg für die Mail">{profil.einstieg}</blockquote>
          ) : (
            <p className="inbox-item-hinweis">Kein belastbarer Anlass: die Mail nutzt den Faktensatz ({'{{Firmenbezug}}'}).</p>
          )}

          {profil.kurzprofil && <><h3>Kurzprofil</h3><p>{profil.kurzprofil}</p></>}

          {liste(profil.anlaesse).length > 0 && (
            <>
              <h3>Anlässe</h3>
              <ul className="kontakt-liste">
                {liste(profil.anlaesse).map((a, i) => (
                  <li key={i}>
                    <strong>{a.datum && <>{a.datum}: </>}{a.titel}</strong>
                    {a.relevanz && <div className="kontakt-detail">{a.relevanz}</div>}
                    {sichereUrl(a.quelle) && <div className="kontakt-detail"><QuellenLink url={a.quelle}>Quelle</QuellenLink></div>}
                  </li>
                ))}
              </ul>
            </>
          )}

          {profil.technik && <><h3>Technische Einordnung</h3><p>{profil.technik}</p></>}
          {profil.hypothese && <><h3>Hypothese</h3><p>{profil.hypothese}</p></>}

          {liste(profil.oeffner).length > 0 && (
            <>
              <h3>Gesprächsöffner</h3>
              <table className="table">
                <thead><tr><th>Beobachtung</th><th>Relevanz</th></tr></thead>
                <tbody>
                  {liste(profil.oeffner).map((o, i) => (
                    <tr key={i}><td>{o.beobachtung}</td><td>{o.relevanz}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {liste(profil.stellen).length > 0 && (
            <>
              <h3>Offene Stellen</h3>
              <ul>
                {liste(profil.stellen).map((s, i) => (
                  <li key={i}><QuellenLink url={s.quelle}>{s.titel}</QuellenLink></li>
                ))}
              </ul>
            </>
          )}

          {liste(profil.wettbewerber).length > 0 && (
            <><h3>Wettbewerber</h3><p>{liste(profil.wettbewerber).join(', ')}</p></>
          )}

          {(profil.intern?.finanzen || profil.intern?.probleme) && (
            <details className="profil-intern">
              <summary>Intern – nur zur Gesprächsvorbereitung, nie in Mails</summary>
              {profil.intern?.finanzen && <p><strong>Finanzen:</strong> {profil.intern.finanzen}</p>}
              {profil.intern?.probleme && <p><strong>Bekannte Probleme:</strong> {profil.intern.probleme}</p>}
            </details>
          )}

          {liste(profil.quellen).length > 0 && (
            <details>
              <summary>Quellen ({liste(profil.quellen).length})</summary>
              <ul>
                {liste(profil.quellen).map((q, i) => (
                  <li key={i}><QuellenLink url={q} /></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
};
